import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, getApiErrorMessage } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import { currency, fmtDate, QUOTATION_STATUSES } from "@/lib/format";
import { Plus, Download, FileText } from "lucide-react";
import { toast } from "sonner";

export default function Quotations({ role }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try { const { data } = await api.get("/quotations"); setItems(data); }
    catch (e) { toast.error(getApiErrorMessage(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try { await api.patch(`/quotations/${id}`, { status }); load(); toast.success("Updated"); }
    catch (e) { toast.error(getApiErrorMessage(e)); }
  };

  const openPdf = (id) => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/quotations/${id}/pdf`, "_blank");
  };

  return (
    <div data-testid="quotations-page">
      <PageHeader
        eyebrow="Estimates"
        title="Quotations"
        subtitle="Treatment estimates shared with international patients. Export as PDF."
        actions={
          role !== "partner" && (
            <Link to={`/${role}/quotations/new`} data-testid="new-quotation-button"
              className="bg-[#2A4D3E] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-[#1E3B2F] flex items-center gap-2">
              <Plus size={15} /> New quotation
            </Link>
          )
        }
      />

      {loading ? <div className="text-sm text-[#5C6660]">Loading…</div> : (
        <div className="bg-white border border-[#E8E6E1] rounded-2xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-[#FDFCFB] border-b border-[#E8E6E1] text-xs font-semibold uppercase tracking-wider text-[#5C6660] text-left">
              <tr>
                <th className="px-5 py-3">Quote #</th>
                <th className="px-5 py-3">Patient</th>
                <th className="px-5 py-3">Treatment</th>
                <th className="px-5 py-3">Total</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3">Created</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((q) => (
                <tr key={q.id} className="border-b border-[#E8E6E1] last:border-0 hover:bg-[#F7F5F2]" data-testid={`quotation-${q.id}`}>
                  <td className="px-5 py-3.5 text-sm font-mono font-semibold text-[#2A4D3E]">{q.quote_number}</td>
                  <td className="px-5 py-3.5 text-sm text-[#2A332C]">{q.patient_name}</td>
                  <td className="px-5 py-3.5 text-sm text-[#5C6660]">{q.treatment}</td>
                  <td className="px-5 py-3.5 text-sm font-semibold text-[#2A332C]">{currency(q.total, q.currency)}</td>
                  <td className="px-5 py-3.5">
                    {role === "admin" || role === "coordinator" ? (
                      <select value={q.status} onChange={(e) => updateStatus(q.id, e.target.value)} data-testid={`q-status-${q.id}`}
                        className="text-[11px] uppercase font-semibold tracking-wider border border-[#E8E6E1] rounded-full px-3 py-1 bg-white focus:outline-none">
                        {QUOTATION_STATUSES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                      </select>
                    ) : <StatusBadge kind="quotation" value={q.status} />}
                  </td>
                  <td className="px-5 py-3.5 text-xs text-[#8E9992]">{fmtDate(q.created_at)}</td>
                  <td className="px-5 py-3.5">
                    <button onClick={() => openPdf(q.id)} data-testid={`pdf-${q.id}`}
                      className="inline-flex items-center gap-1.5 text-xs text-[#2A4D3E] font-semibold hover:underline">
                      <Download size={13} /> PDF
                    </button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan="7" className="px-5 py-12 text-center text-sm text-[#8E9992]">No quotations yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
